import React, { useState } from 'react';
import {
  Box,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Fab,
  Tooltip,
  Badge,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add,
  FileUpload,
  Image,
  EmojiEmotions,
  Send,
  MoreVert,
  Menu as MenuIcon,
  Share,
  Download,
  Print,
  ContentCopy,
  Delete,
  Archive,
  Star,
  StarBorder,
  Settings,
  Clear,
} from '@mui/icons-material';

function ChatQuickActions({
  onNewConversation,
  onClearChat,
  onFileUpload,
  onImageUpload,
  onVoiceInput,
  onEmojiPicker,
  onShare,
  onExport,
  onPrint,
  onCopy,
  onDelete,
  onArchive,
  onFavorite,
  onSettings,
  isFavorite = false,
  hasAttachments = false,
  canShare = true,
}) {
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const actions = [
    {
      icon: <Add />,
      name: 'New Chat',
      action: onNewConversation,
      color: 'primary',
    },
    {
      icon: <FileUpload />,
      name: 'Upload File',
      action: onFileUpload,
      color: 'secondary',
    },
    {
      icon: <Image />,
      name: 'Upload Image',
      action: onImageUpload,
      color: 'info',
    },
    {
      icon: <EmojiEmotions />,
      name: 'Emoji',
      action: onEmojiPicker,
      color: 'success',
    },
  ];

  const secondaryActions = [
    {
      icon: <Settings />,
      name: 'Settings',
      action: onSettings,
    },
    {
      icon: <Clear />,
      name: 'Clear Chat',
      action: onClearChat,
    },
    {
      icon: <Share />,
      name: 'Share',
      action: onShare,
      disabled: !canShare,
    },
    {
      icon: <Download />,
      name: 'Export',
      action: onExport,
    },
    {
      icon: <Print />,
      name: 'Print',
      action: onPrint,
    },
    {
      icon: <ContentCopy />,
      name: 'Copy',
      action: onCopy,
    },
    {
      icon: isFavorite ? <Star /> : <StarBorder />,
      name: isFavorite ? 'Unfavorite' : 'Favorite',
      action: onFavorite,
      color: isFavorite ? 'warning' : 'default',
    },
    {
      icon: <Archive />,
      name: 'Archive',
      action: onArchive,
    },
    {
      icon: <Delete />,
      name: 'Delete',
      action: onDelete,
      color: 'error',
    },
  ];

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuAction = (action) => {
    if (action && typeof action === 'function') {
      action();
    }
    handleMenuClose();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Main SpeedDial */}
      <SpeedDial
        ariaLabel="Chat quick actions"
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          '& .MuiFab-primary': {
            width: 56,
            height: 56,
          },
        }}
        icon={<SpeedDialIcon />}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        direction="up"
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={() => {
              action.action();
              setOpen(false);
            }}
            sx={{
              bgcolor: `${action.color}.main`,
              color: `${action.color}.contrastText`,
              '&:hover': {
                bgcolor: `${action.color}.dark`,
              },
            }}
          />
        ))}
      </SpeedDial>

      {/* Secondary actions - More options */}
      <Tooltip title="More options" placement="right">
        <Fab
          size="small"
          sx={{
            position: 'fixed',
            top: 80,
            left: 80,
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={handleMenuOpen}
        >
          <Badge
            badgeContent={hasAttachments ? 1 : 0}
            color="primary"
            invisible={!hasAttachments}
          >
            <MenuIcon />
          </Badge>
        </Fab>
      </Tooltip>

      {/* Dropdown Menu for Secondary Actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            boxShadow: 3,
            '& .MuiMenuItem-root': {
              py: 1,
              px: 2,
            },
          },
        }}
      >
        {secondaryActions.map((action, index) => (
          <React.Fragment key={action.name}>
            <MenuItem
              onClick={() => handleMenuAction(action.action)}
              disabled={action.disabled}
              sx={{
                color: action.color === 'error' ? 'error.main' : 'inherit',
                '&:hover': {
                  bgcolor: action.color === 'error' ? 'error.light' : 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                {action.icon}
              </ListItemIcon>
              <ListItemText primary={action.name} />
            </MenuItem>
            {index === 3 && <Divider />}
            {' '}
            {/* Add divider after Copy */}
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
}

export default ChatQuickActions;
